import React, { useState, useRef, useEffect } from "react";
import { FaPaperPlane, FaFile } from "react-icons/fa";
import { auth, db, storage } from "../../Auth/firebase";
import {
  collection,
  query,
  onSnapshot,
  addDoc,
  serverTimestamp,
  orderBy,
  where,
  doc,
  deleteDoc,
  getDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import Sidebar from "./sidebar";
import NewChannelModal from "./NewChannelModal";
import ConfirmModal from "../../../Modals/ConfirmModal";
import {
  fetchUserDetails,
  isUserOnline,
} from "../../../utils/fetchUserDetails";
import emailjs from "emailjs-com";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useProjects } from "../../../context/ProjectsContext";

const uploadFile = async (file) => {
  const storageRef = ref(storage, `files/${file.name}_${Date.now()}`);
  await uploadBytes(storageRef, file);
  const downloadURL = await getDownloadURL(storageRef);
  return downloadURL;
};

const Channels = () => {
  const [inputs, setInputs] = useState({});
  const [messages, setMessages] = useState([]);
  const [activeChannelId, setActiveChannelId] = useState(null);
  const [channels, setChannels] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newChannelName, setNewChannelName] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [channelToDelete, setChannelToDelete] = useState(null);
  const { currentUser } = auth;
  const { activeProject } = useProjects(); // Get the active project from context
  const navigate = useNavigate();
  const endOfMessagesRef = useRef(null);
  const [channelMembersDetails, setChannelMembersDetails] = useState([]);

  useEffect(() => {
    if (!activeProject) return;

    const usersQuery = query(collection(db, "users"));
    const unsubscribeUsers = onSnapshot(usersQuery, (snapshot) => {
      const users = snapshot.docs
        .map((doc) => ({
          value: doc.id,
          label: doc.data().displayName || "Anonymous",
          photoURL: doc.data().profileImage || "default_avatar_url_here",
        }))
        .filter(
          (user) =>
            user.value !== currentUser.uid &&
            (activeProject.assignees.includes(user.value) ||
              activeProject.creator.uid === user.value)
        );
      setAllUsers(users);
    });

    return () => unsubscribeUsers();
  }, [currentUser.uid, activeProject, db, setAllUsers]);

  useEffect(() => {
    if (messages.length > 0 && endOfMessagesRef.current) {
      endOfMessagesRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, [messages]);

  useEffect(() => {
    if (!activeProject) return;

    const channelsQuery = query(
      collection(db, "channels"),
      where("projectId", "==", activeProject.id),
      where("members", "array-contains", currentUser.uid)
    );
    const unsubscribeChannels = onSnapshot(channelsQuery, (querySnapshot) => {
      const channelsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        name: doc.data().name,
        members: doc.data().members,
        createdBy: doc.data().createdBy,
      }));
      setChannels(channelsData);
    });
    return () => unsubscribeChannels();
  }, [activeProject, currentUser.uid]);

  useEffect(() => {
    if (!activeChannelId) {
      setMessages([]);
      setChannelMembersDetails([]);
      return;
    }
    const q = query(
      collection(db, "messages"),
      where("type", "==", "channel"),
      where("targetId", "==", activeChannelId),
      orderBy("timestamp", "asc")
    );
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const newMessages = querySnapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
        username: doc.data().user.username,
        isUser: doc.data().user.uid === currentUser.uid,
      }));
      setMessages(newMessages);
    });
    return () => unsubscribe();
  }, [activeChannelId, currentUser.uid]);

  useEffect(() => {
    async function fetchChannelDetails() {
      if (!activeChannelId) return;

      const channelDoc = await getDoc(doc(db, "channels", activeChannelId));
      if (channelDoc.exists()) {
        const members = channelDoc.data().members;
        const membersDetails = await Promise.all(members.map(fetchUserDetails));
        const membersDetailsWithStatus = membersDetails.map((member) => ({
          ...member,
          isOnline: isUserOnline(member.id),
        }));
        setChannelMembersDetails(membersDetailsWithStatus);
      }
    }

    fetchChannelDetails();
  }, [activeChannelId]);

  const handleSend = async (text = null, fileUrl = null) => {
    const messageText = text || inputs[activeChannelId]?.trim();
    if (!messageText && !fileUrl) return;

    const messageData = {
      text: fileUrl ? `File: ${messageText}` : messageText,
      fileUrl,
      type: "channel",
      targetId: activeChannelId,
      user: {
        uid: currentUser.uid,
        username: currentUser.displayName || "Anonmous",
      },
      timestamp: serverTimestamp(),
    };

    try {
      await addDoc(collection(db, "messages"), messageData);

      const channelDoc = await getDoc(doc(db, "channels", activeChannelId));
      if (channelDoc.exists()) {
        const memberIds = channelDoc.data().members;
        const memberDetails = await Promise.all(
          memberIds.map(fetchUserDetails)
        );

        memberDetails.forEach((member) => {
          if (member.email && member.uid !== currentUser.uid) {
            const emailParams = {
              to_name: member.username,
              to_email: member.email,
              from_name: currentUser.displayName || "Your Application",
              message: messageText,
            };

            emailjs
              .send(
                "service_wz2z4do",
                "template_g5c3lbt",
                emailParams,
                "oMXWMelsfkS3iY97x"
              )
              .then((response) => {
                console.log(
                  "Email sent successfully!",
                  response.status,
                  response.text
                );
              })
              .catch((error) => {
                console.error("Failed to send email:", error);
              });
          }
        });
      }

      setInputs((prevInputs) => ({ ...prevInputs, [activeChannelId]: "" }));
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const downloadURL = await uploadFile(file);
    handleSend(file.name, downloadURL);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e, channelId) => {
    const { value } = e.target;
    setInputs((prevInputs) => ({ ...prevInputs, [channelId]: value }));
  };

  const handleNewChannel = async () => {
    if (!newChannelName.trim() || selectedUsers.length === 0) return;
    const channelDocRef = await addDoc(collection(db, "channels"), {
      name: newChannelName,
      members: [currentUser.uid, ...selectedUsers.map((user) => user.value)],
      createdBy: currentUser.uid, // Add the createdBy field
      projectId: activeProject.id, // Link channel to the active project
    });
    setNewChannelName("");
    setSelectedUsers([]);
    closeModal();
  };

  const openModal = () => setShowModal(true);
  const closeModal = () => setShowModal(false);

  const openConfirmModal = (channelId) => {
    setChannelToDelete(channelId);
    setConfirmModalOpen(true);
  };

  const closeConfirmModal = () => {
    setChannelToDelete(null);
    setConfirmModalOpen(false);
  };

  const handleDeleteChannel = async () => {
    if (!channelToDelete) return;

    try {
      await deleteDoc(doc(db, "channels", channelToDelete));
      setChannelToDelete(null);
      setConfirmModalOpen(false);
    } catch (error) {
      console.error("Error deleting channel:", error);
    }
  };

  const getTimeString = (timestamp) => {
    if (!timestamp) return "";

    const date = new Date(timestamp.seconds * 1000);
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const formattedHours = hours % 12 === 0 ? 12 : hours % 12;

    return `${formattedHours}:${minutes} ${ampm}`;
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar
        channels={channels}
        activeChannelId={activeChannelId}
        setActiveChannelId={setActiveChannelId}
        openModal={openModal}
        openConfirmModal={openConfirmModal}
        channelMembersDetails={channelMembersDetails}
        currentUser={currentUser}
        activeProject={activeProject} // Pass activeProject to Sidebar
      />

      <div className="w-3/4 flex h-[90vh] flex-col overflow-hidden">
        {activeChannelId ? (
          <>
            <div className="flex-1 overflow-y-auto p-8">
              {messages.length > 0 ? (
                messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex items-start mb-4 ${
                      message.isUser ? "justify-end" : "justify-start"
                    }`}
                  >
                    <div
                      className={`${
                        message.isUser
                          ? "bg-blue-500 text-white"
                          : "bg-gray-300 text-gray-800"
                      } p-3 rounded-lg max-w-md`}
                    >
                      <div className="text-sm font-semibold mt-1">
                        {message.username}
                      </div>
                      {message.fileUrl ? (
                        <a
                          href={message.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-white underline"
                        >
                          {message.text}
                        </a>
                      ) : (
                        <p>{message.text}</p>
                      )}
                      <div
                        className={`text-xs mt-1 ${
                          message.isUser ? "text-white" : "text-gray-500"
                        }`}
                      >
                        {getTimeString(message.timestamp)}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex-1 flex items-center justify-center p-8">
                  <p className="text-gray-500">
                    No messages yet. Start the conversation!
                  </p>
                </div>
              )}
              <div ref={endOfMessagesRef} />
            </div>
            <div className="h-20 p-4 border-t flex items-center gap-2 bg-white">
              <textarea
                className="flex-1 p-2 border rounded-md focus:outline-none focus:ring focus:border-blue-300 resize-none"
                placeholder="Type your message..."
                value={inputs[activeChannelId] || ""}
                onChange={(e) => handleInputChange(e, activeChannelId)}
                onKeyPress={handleKeyPress}
              />
              <button
                onClick={handleSend}
                className="ml-4 w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center hover:bg-blue-600"
              >
                <FaPaperPlane />
              </button>
              <input
                type="file"
                id="fileInput"
                className="hidden"
                onChange={handleFileChange}
              />
              <label
                htmlFor="fileInput"
                className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center cursor-pointer hover:bg-gray-400"
              >
                <FaFile />
              </label>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-gray-500">Select a channel to start chatting.</p>
          </div>
        )}
      </div>

      <NewChannelModal
        showModal={showModal}
        closeModal={closeModal}
        newChannelName={newChannelName}
        setNewChannelName={setNewChannelName}
        allUsers={allUsers}
        setSelectedUsers={setSelectedUsers}
        selectedUsers={selectedUsers}
        handleNewChannel={handleNewChannel}
      />

      <ConfirmModal
        isOpen={confirmModalOpen}
        onRequestClose={closeConfirmModal}
        onConfirm={handleDeleteChannel}
        message="Are you sure you want to delete this channel?"
      />
    </div>
  );
};

export default Channels;
